import React, { useState, useEffect } from "react";

interface CaptionedImageProps {
  src: string;
  alt: string;
  caption?: string;
  maxWidth?: string | number;
  captionPosition?: "bottom" | "right";
  /**
   * Optional aspect ratio. Pass a string (e.g. "16/9") for all screens, or
   * an object keyed by Tailwind breakpoint (e.g. { base: "1/1", md: "16/9" })
   * to specify different ratios for mobile / desktop. The special key
   * "base" (or "default") applies with no breakpoint prefix.
   */
  aspectRatio?: string | Record<string, string>;
}

export const LightboxImage: React.FC<CaptionedImageProps> = ({
  src,
  alt,
  caption,
  maxWidth,
  captionPosition = "bottom",
  aspectRatio,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // ---- Responsive aspect-ratio handling ----
  const getResponsiveRatio = (
    ratioMap: Record<string, string>
  ): string | undefined => {
    if (typeof window === "undefined") return undefined;

    const breakpoints: Record<string, number> = {
      base: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
      "2xl": 1536,
    };

    // Start with base/default if provided
    let chosen: string | undefined = ratioMap["base"] || ratioMap["default"];

    // Iterate through breakpoints in ascending order
    for (const [bp, minW] of Object.entries(breakpoints)) {
      if (window.innerWidth >= minW && ratioMap[bp]) {
        chosen = ratioMap[bp];
      }
    }
    return chosen;
  };

  const [computedRatio, setComputedRatio] = useState<string | undefined>(() => {
    if (typeof aspectRatio === "string") return aspectRatio;
    if (typeof aspectRatio === "object") {
      return getResponsiveRatio(aspectRatio);
    }
    return undefined;
  });

  useEffect(() => {
    if (typeof aspectRatio !== "object") return;

    const handleResize = () => {
      const newRatio = getResponsiveRatio(aspectRatio);
      setComputedRatio((prev) => (prev === newRatio ? prev : newRatio));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [aspectRatio]);

  const openLightbox = () => setIsOpen(true);
  const closeLightbox = () => setIsOpen(false);

  const figureStyle = {
    maxWidth: maxWidth,
  };

  // Determine the figure layout based on captionPosition instead of returning early so that
  // the lightbox modal can still be rendered alongside the figure.
  const figureContent =
    captionPosition === "right" ? (
      // Layout for caption on the right: stack vertically on small screens and side-by-side on larger ones.
      <figure
        style={figureStyle}
        className="mx-auto my-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-start w-full cursor-pointer"
        onClick={openLightbox}
      >
        <img
          src={src}
          alt={alt}
          className="rounded-md w-full h-auto object-contain"
        />
        <figcaption className="text-sm italic text-center md:text-left self-center md:self-start text-gray-800 dark:text-gray-300">
          {caption}
        </figcaption>
      </figure>
    ) : (
      // Default layout for caption at the bottom
      <figure
        style={{
          ...figureStyle,
          ...(computedRatio ? { aspectRatio: computedRatio } : {}),
          display: "flex",
          flexDirection: "column",
        }}
        className="mx-auto my-4 cursor-pointer w-full"
        onClick={openLightbox}
      >
        {computedRatio ? (
          <img
            src={src}
            alt={alt}
            className="rounded-md w-full h-full object-contain flex-1"
          />
        ) : (
          <img src={src} alt={alt} className="rounded-md w-full h-auto" />
        )}
        {caption && (
          <figcaption className="mt-2 text-sm text-center text-gray-600 dark:text-gray-300 italic">
            {caption}
          </figcaption>
        )}
      </figure>
    );

  return (
    <>
      {figureContent}

      {/* Lightbox Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50"
          onClick={closeLightbox}
        >
          <div
            className="relative bg-white dark:bg-gray-900 rounded-lg max-w-[90vw] max-h-[90vh] px-8 py-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-0 right-2 text-black dark:text-white text-2xl font-bold"
              onClick={closeLightbox}
            >
              &times;
            </button>
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-[85vh] object-contain"
            />
            {caption && (
              <div className="mt-2 text-sm text-center text-gray-800 dark:text-gray-300 italic">
                {caption}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
